/**
 * "Jump to latest" for bottom-anchored web streams: a hook that keeps the
 * stream pinned to its newest item while the reader is there, stops following
 * the moment they scroll up, and counts what arrives meanwhile, plus the pill
 * that takes them back. Scroll handling stays off React state except when the
 * reader crosses the near-bottom threshold.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type MutableRefObject } from 'react';
import { ArrowDown } from './icons';
import { Button } from './button';
import { cn } from './utils';
import { distanceFromBottom, jumpToLatestAccessibleLabel } from '../../shared-ui/scroll/jumpToLatest';
import { useJumpToLatestIndicator, type JumpToLatestIndicatorOptions } from '../../shared-ui/scroll/useJumpToLatestIndicator';

/** Within this many pixels of the end the reader counts as at the bottom again. */
const FOLLOW_SLOP = 4;

const metricsOf = (el: HTMLElement) => ({ offset: el.scrollTop, viewport: el.clientHeight, content: el.scrollHeight });

export interface UseJumpToLatestOptions extends JumpToLatestIndicatorOptions {
  /** Mirrors the scroll element into a ref the caller already reads elsewhere. */
  scrollElementRef?: MutableRefObject<HTMLDivElement | null>;
}

export function useJumpToLatest({ scrollElementRef, ...indicatorOptions }: UseJumpToLatestOptions) {
  const indicator = useJumpToLatestIndicator(indicatorOptions);
  const { report, markAtBottom } = indicator;
  const [scrollElement, setScrollElement] = useState<HTMLDivElement | null>(null);
  const [contentElement, setContentElement] = useState<HTMLElement | null>(null);
  // Following is stricter than "near": any upward scroll by the reader stops
  // it, so streamed text cannot drag them back down while they read.
  const followingRef = useRef(true);
  const lastOffsetRef = useRef(0);
  const frameRef = useRef<number | null>(null);

  const scrollRef = useCallback((node: HTMLDivElement | null) => {
    if (scrollElementRef) scrollElementRef.current = node;
    setScrollElement(node);
  }, [scrollElementRef]);

  const pin = useCallback(() => {
    if (!scrollElement) return;
    scrollElement.scrollTop = scrollElement.scrollHeight;
    lastOffsetRef.current = scrollElement.scrollTop;
  }, [scrollElement]);

  useEffect(() => {
    if (!scrollElement) return;
    lastOffsetRef.current = scrollElement.scrollTop;
    const onScroll = () => {
      const offset = scrollElement.scrollTop;
      if (offset < lastOffsetRef.current - 1) followingRef.current = false;
      if (distanceFromBottom(metricsOf(scrollElement)) <= FOLLOW_SLOP) followingRef.current = true;
      lastOffsetRef.current = offset;
      if (frameRef.current !== null) return;
      frameRef.current = requestAnimationFrame(() => {
        frameRef.current = null;
        report(metricsOf(scrollElement));
      });
    };
    scrollElement.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      scrollElement.removeEventListener('scroll', onScroll);
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
    };
  }, [scrollElement, report]);

  const scopeRef = useRef(indicatorOptions.scope);
  useLayoutEffect(() => {
    if (scopeRef.current !== indicatorOptions.scope) {
      scopeRef.current = indicatorOptions.scope;
      followingRef.current = true;
    }
    if (followingRef.current) pin();
  }, [indicatorOptions.itemCount, indicatorOptions.latestKey, indicatorOptions.scope, pin]);

  // Streamed replies and late layout (images, markdown) grow the content
  // without adding items; keep the reader on the newest line while following.
  useEffect(() => {
    if (!contentElement || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => { if (followingRef.current) pin(); });
    observer.observe(contentElement);
    return () => observer.disconnect();
  }, [contentElement, pin]);

  const jumpToLatest = useCallback((behavior: ScrollBehavior = 'smooth') => {
    followingRef.current = true;
    markAtBottom();
    if (!scrollElement) return;
    if (behavior === 'smooth' && typeof scrollElement.scrollTo === 'function') {
      scrollElement.scrollTo({ top: scrollElement.scrollHeight, behavior });
    } else {
      pin();
    }
  }, [markAtBottom, pin, scrollElement]);

  return {
    scrollRef,
    contentRef: setContentElement,
    nearBottom: indicator.nearBottom,
    unseen: indicator.unseen,
    jumpToLatest,
  };
}

export interface JumpToLatestButtonProps {
  unseen: number;
  onClick: () => void;
  noun?: { one: string; many: string };
  className?: string;
  style?: CSSProperties;
}

/** Glass pill; the caller positions it (absolute, clear of its composer). */
export function JumpToLatestButton({ unseen, onClick, noun, className, style }: JumpToLatestButtonProps) {
  return (
    <Button
      type="button"
      variant="ghost"
      onClick={onClick}
      aria-label={jumpToLatestAccessibleLabel(unseen, noun)}
      // Centred just above the composer, as ChatGPT's scroll-down button: a round arrow on a raised
      // white surface with a hairline and soft shadow. The unseen count is in its accessible name.
      className={cn('absolute left-1/2 z-20 flex -translate-x-1/2 items-center justify-center rounded-full bg-surface-raised p-0 hover:bg-surface-raised', className)}
      style={{
        width: 32,
        height: 32,
        boxShadow: 'var(--composer-shadow)',
        border: '1px solid var(--border)',
        color: 'var(--foreground)',
        ...style,
      }}
    >
      <ArrowDown aria-hidden="true" style={{ width: 16, height: 16 }} />
    </Button>
  );
}
